import React, { useEffect, useMemo, useState } from "react";
import { flatten, map, reduce } from "lodash";
import { DragDropProvider } from "@dnd-kit/react";
import { move } from "@dnd-kit/helpers";
import KanbanColumn, { KanbanColumnProps } from "./KanbanColumn";
import { KanbanItemProps } from "./KanbanItem";
import { Center, Row } from "@/shared/ui/Blocks";
import Circles from "@/shared/ui/Loaders/Circles";

export interface KanbanColumnsState {
  [columnId: string]: KanbanItemProps[];
}

export interface ChangedKanbanItem {
  columnId?: string;
  itemId?: string;
  order?: number;
}

export interface KanbanProps {
  loading?: boolean;
  columns: KanbanColumnProps[];
  renderItem: (item: KanbanItemProps) => React.ReactElement;
  onDataChange?: (
    change: ChangedKanbanItem,
    columnsState: KanbanColumnsState
  ) => void;
}

export const moveKanbanItem = (items: KanbanColumnsState, event: any) => {
  const newState = move(items, event);
  return reduce(
    newState,
    (acc, value, key) => ({
      ...acc,
      [key]: map(value, (item, index) => ({
        ...item,
        columnId: key,
        order: index,
      })),
    }),
    {}
  );
};

export const findChangedItem = (
  itemId: string,
  columnsState: KanbanColumnsState
) => {
  const values = flatten(Object.values(columnsState));
  const changedItem = values.find((item) => item.id === itemId);

  return {
    columnId: changedItem?.columnId,
    itemId: changedItem?.id,
    order: changedItem?.order,
  };
};

function Kanban(props: KanbanProps) {
  const { columns, renderItem, onDataChange, loading } = props;

  const initialColumns = useMemo(
    () =>
      reduce(
        columns,
        (acc, value) => ({
          ...acc,
          [value.id]: value.items || ([] as KanbanItemProps[]),
        }),
        {} as KanbanColumnsState
      ),
    [columns]
  );

  const [kanbanItems, setKanbanItems] = useState(initialColumns);

  useEffect(() => {
    if (!loading && initialColumns) {
      setKanbanItems(initialColumns);
    }
  }, [loading, initialColumns]);

  if (loading) {
    return (
      <Center className="">
        <Circles />
      </Center>
    );
  }

  return (
    <DragDropProvider
      onDragOver={(event) => {
        const { source, target } = event.operation;
        setKanbanItems((items) => moveKanbanItem(items, event));
      }}
      onDragEnd={(event) => {
        const { source, target, ...rest } = event.operation;

        const itemId = source?.id?.toString() || "";

        setKanbanItems((items) => {
          const newState = moveKanbanItem(items, event);

          const change = findChangedItem(itemId, newState);

          onDataChange && onDataChange(change, newState);
          return newState;
        });
      }}
    >
      <Row className="flex-1 w-full gap-4 overflow-x-auto px-8">
        {map(columns, (column) => {
          const columnItems = (kanbanItems[column.id] || []).filter(
            (i) => !!i.id
          );
          return (
            <KanbanColumn
              key={column.id}
              {...column}
              renderItem={renderItem}
              items={columnItems}
            />
          );
        })}
      </Row>
    </DragDropProvider>
  );
}

export default Kanban;
